import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import { useIntl } from 'react-intl'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { rem } from 'polished'
import { type ChangeEvent, useCallback, useState } from 'react'
import styled from 'styled-components'

const BannerContainer = styled.div`
  width: 100%;
  padding-top: ${rem(70)};

  & > :first-child {
    width: 100%;
    height: 100%;
  }

  ${mediaQueryUp.desktop} {
    padding-top: 0;
    height: ${rem(144)};
  }
`

const Banner = styled(Sheet)`
  width: 100%;
  height: 100%;
`

const ButtonsContainer = styled(Group)`
  flex: 1;
`

const StyledTextInputOnly = styled(TextInputOnly)`
  min-width: ${rem(220)};
`

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${rem(-50)};
  width: 100%;


  ${mediaQueryUp.desktop} {
    margin-top: 0;
    width: auto;
  }
`

const MIN_AMOUNT = 50

interface Props {
  onGiftClick?: (amount: number) => void
}

function WeddingGiftCardBanner(props: Props) {
  const { onGiftClick } = props
  const intl = useIntl()
  const currencyFormatter = useCurrencyFormatter()
  const formattedMinAmount = currencyFormatter(MIN_AMOUNT)
  const [amount, setAmount] = useState('')

  const handleAmountChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value)
  }, [])

  const handleGiftClick = useCallback(() => {
    const selectedAmount = amount ? Number(amount) : MIN_AMOUNT
    onGiftClick?.(selectedAmount)
  }, [onGiftClick, amount])

  return (
    <BannerContainer>
      <PaletteSheet paletteType="inverse">
        <Banner background="normal-background" padding="0 20 28 20" desktopPadding="0 28" borderRadius="L">
          <Group direction="vertical" desktopDirection="horizontal" gap={20} verticalAlign="center" fullHeight>
            <ImageContainer>
              <Image
                id="gift-card_3rq3ya"
                alt={intl.formatMessage({ defaultMessage: 'Wedding Gift Card', id: '5PYmcANrSLuws1oTd9EU', description: 'Alt text for the gift card image in the wedding gift card banner.' })}
                width={180}
              />
            </ImageContainer>
            <Group direction="vertical" gap={8} verticalAlign="center" desktopVerticalAlign="start">
              <Heading variant="heading3alt" colour="normal-contrast" align="center" desktopAlign="start">
                {intl.formatMessage({ defaultMessage: 'Not sure what to choose?', id: 'VygD168ORBSDVcJ8X5M0', description: 'Heading for the wedding gift card contribution banner.' })}
              </Heading>
              <BodyText variant="large" colour="normal-contrast" align="center" desktopAlign="start">
                {intl.formatMessage({ defaultMessage: 'A Luxury Escapes gift card works perfectly for any part of their plans.', id: 'ig-O0QV7rrCzatccPQ7A', description: 'Body copy explaining the wedding gift card contribution option.' })}
              </BodyText>
            </Group>
            <ButtonsContainer direction="vertical" desktopDirection="horizontal" gap={20} verticalAlign="center" horizontalAlign="stretch" desktopHorizontalAlign="end">
              <StyledTextInputOnly
                placeholder={intl.formatMessage(
                  { defaultMessage: 'Input your amount. e.g. {formattedMinAmount}', id: 'QsVvzKKKYe6ygeTZ-i5d', description: 'Placeholder for custom wedding gift card amount input.' },
                  { formattedMinAmount },
                )}
                value={amount}
                onChange={handleAmountChange}
                type="number"
                noValidationMessage
                noValidationSpacing
              />
              <TextButton kind="primary" size="large" onClick={handleGiftClick}>
                {intl.formatMessage({ defaultMessage: 'Gift now', id: 'G1DqmTrKNpCXlQ1rrYhb', description: 'Button label for contributing a wedding gift card amount.' })}
              </TextButton>
            </ButtonsContainer>
          </Group>
        </Banner>
      </PaletteSheet>
    </BannerContainer>
  )
}

export default WeddingGiftCardBanner
